import React, { useMemo, useState, useRef, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { ChevronLeftIcon, ChevronRightIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/outline';
import ColumnFilter from './ColumnFilter';

// Reusable TanStack Table with selection, sorting, server pagination hooks
const TanTable = ({
  columns,
  data,
  page,
  pageSize,
  rowCount,
  onPageChange,
  onPageSizeChange,
  sorting,
  onSortingChange,
  selectedRowIds,
  onSelectedRowIdsChange,
  getRowId,
  renderRowActions,
  showSelection = true,
  loading = false,
  showFilterRow = true,
  filterDefs = [],
  filters = {},
  onFiltersChange,
  // New column filter props
  columnFilters = {},
  onColumnFilterChange,
  enableColumnFilters = false,
  columnFormatters = {} // New prop for column-specific formatters
}) => {
  const [columnSizing, setColumnSizing] = useState({});
  const [isResizing, setIsResizing] = useState(false);
  const memoColumns = useMemo(() => columns, [columns]);
  const memoData = useMemo(() => data, [data]);

  // Function to determine data type for column filters
  const getColumnDataType = (accessorKey) => {
    const dataTypes = {
      // Date columns (only actual date fields, not formatted text)
      'date': 'date',
      'created_at': 'date',
      'updated_at': 'date',
      
      // Number range columns (need From/To fields)
      'amount_rs': 'number_range',
      'amount_usd': 'number_range',
      
      // Boolean columns (need Yes/No dropdown)
      'request_inward': 'boolean',
      'invoice_file': 'boolean', // Yes = has file, No = no file
      
      // Text columns (including formatted month display)
      'reflecting_month': 'text', // Changed from 'date' to 'text' since it's displayed as formatted text
      'source': 'text',
      'bank': 'text',
      'reflecting_bank': 'text',
      'remark': 'text',
      'expense': 'text',
      'vendor': 'text',
      'category': 'text',
      'sub_category': 'text',
      'mode': 'text',
      'nature': 'text',
      'deducted_from': 'text'
    };
    
    return dataTypes[accessorKey] || 'text';
  };

  const table = useReactTable({
    data: memoData,
    columns: memoColumns,
    state: {
      sorting,
      columnSizing,
    },
    onSortingChange,
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId,
    manualPagination: false, // Use client-side pagination for filtered data
    manualSorting: false,    // Use client-side sorting for filtered data
    pageCount: Math.ceil((rowCount || 0) / (pageSize || 10)),
  });

  const allSelected = showSelection && memoData.length > 0 && (selectedRowIds || []).length > 0 && memoData.every((row) => (selectedRowIds || []).includes(getRowId ? getRowId(row) : row.id));
  const onToggleAll = (checked) => {
    if (!showSelection || !onSelectedRowIdsChange) return;
    if (checked) {
      onSelectedRowIdsChange(memoData.map((r) => (getRowId ? getRowId(r) : r.id)));
    } else {
      onSelectedRowIdsChange([]);
    }
  };

  return (
    <div className="w-full">
      {loading && (
        <div className="w-full h-1 bg-gradient-to-r from-blue-100 to-blue-200 rounded-full overflow-hidden mb-4">
          <div className="h-1 bg-gradient-to-r from-blue-500 to-blue-600 animate-pulse rounded-full" style={{ width: '60%' }}></div>
        </div>
      )}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
          <table 
            className="min-w-full bg-white" 
            style={{ width: table.getCenterTotalSize() }}
          >
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              {showSelection && onSelectedRowIdsChange && (
                <th className="px-3 py-2 w-10 sticky top-0 bg-gray-50">
                  <input type="checkbox" checked={allSelected} onChange={(e)=> onToggleAll(e.target.checked)} />
                </th>
              )}
              {table.getHeaderGroups().map((headerGroup) => (
                <React.Fragment key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th 
                      key={header.id} 
                      className="px-4 py-3 text-left text-sm font-semibold text-gray-700 select-none sticky top-0 bg-gradient-to-b from-gray-50 to-gray-100 border-r border-gray-200 transition-colors relative group" 
                      style={{ width: header.getSize() }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div 
                          className="flex items-center gap-1 cursor-pointer hover:text-gray-900 flex-1"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() === 'asc' && <span className="text-blue-600 text-xs ml-1">↑</span>}
                          {header.column.getIsSorted() === 'desc' && <span className="text-blue-600 text-xs ml-1">↓</span>}
                        </div>
                        {/* Column Filter */}
                        {enableColumnFilters && onColumnFilterChange && header.column.columnDef.accessorKey && (
                          <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                            <ColumnFilter
                              column={header.column.columnDef.accessorKey}
                              dataType={getColumnDataType(header.column.columnDef.accessorKey)}
                              onFilter={(filter) => onColumnFilterChange(header.column.columnDef.accessorKey, filter)}
                              currentFilter={columnFilters[header.column.columnDef.accessorKey]}
                              data={memoData}
                              isActive={!!columnFilters[header.column.columnDef.accessorKey]}
                              formatValue={columnFormatters[header.column.columnDef.accessorKey]}
                            />
                          </div>
                        )}
                        {/* Column Resizer */}
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className={`absolute right-0 top-0 h-full w-1 bg-gray-300 cursor-col-resize select-none touch-none opacity-0 group-hover:opacity-100 hover:bg-blue-500 transition-all ${
                            header.column.getIsResizing() ? 'bg-blue-500 opacity-100' : ''
                          }`}
                        />
                      </div>
                    </th>
                  ))}
                  {renderRowActions && <th className="px-3 py-2 text-sm font-medium text-gray-700 sticky top-0 bg-gray-50">Actions</th>}
                </React.Fragment>
              ))}
            </tr>
            {showFilterRow && (
              <tr>
                {showSelection && onSelectedRowIdsChange && <th className="px-3 py-2 w-10 sticky top-[42px] bg-white"></th>}
                {memoColumns.map((col) => {
                  const def = filterDefs.find((d)=> d.id === (col.accessorKey || col.id));
                  if (!def) return <th key={`filter-${col.accessorKey || col.id}` } className="px-3 py-2 sticky top-[42px] bg-white"></th>;
                  const val = filters?.[def.id];
                  
                  if (def.type === 'numberRange') {
                    return (
                      <th key={`filter-${def.id}` } className="px-3 py-2 sticky top-[42px] bg-white">
                        <div className="flex gap-1">
                          <input className="w-1/2 px-2 py-1 border border-gray-200 rounded text-sm" placeholder="Min" value={val?.min ?? ''} onChange={(e)=> onFiltersChange?.({ ...filters, [def.id]: { ...(val||{}), min: e.target.value } })} />
                          <input className="w-1/2 px-2 py-1 border border-gray-200 rounded text-sm" placeholder="Max" value={val?.max ?? ''} onChange={(e)=> onFiltersChange?.({ ...filters, [def.id]: { ...(val||{}), max: e.target.value } })} />
                        </div>
                      </th>
                    );
                  }
                  
                  if (def.type === 'boolean') {
                    return (
                      <th key={`filter-${def.id}` } className="px-3 py-2 sticky top-[42px] bg-white">
                        <select className="w-full px-2 py-1 border border-gray-200 rounded text-sm" value={val ?? ''} onChange={(e)=> onFiltersChange?.({ ...filters, [def.id]: e.target.value })}>
                          <option value="">All</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </th>
                    );
                  }
                  
                  if (def.type === 'select') {
                    return (
                      <th key={`filter-${def.id}` } className="px-3 py-2 sticky top-[42px] bg-white">
                        <select className="w-full px-2 py-1 border border-gray-200 rounded text-sm" value={val ?? ''} onChange={(e)=> onFiltersChange?.({ ...filters, [def.id]: e.target.value })}>
                          <option value="">All</option>
                          {def.options?.map(option => (
                            <option key={option} value={option}>
                              {def.optionLabels?.[option] || option}
                            </option>
                          ))}
                        </select>
                      </th>
                    );
                  }
                  
                  // default text
                  return (
                    <th key={`filter-${def.id}` } className="px-3 py-2 sticky top-[42px] bg-white">
                      <input className="w-full px-2 py-1 border border-gray-200 rounded text-sm" placeholder="Contains" value={val ?? ''} onChange={(e)=> onFiltersChange?.({ ...filters, [def.id]: e.target.value })} />
                    </th>
                  );
                })}
                {renderRowActions && <th className="px-3 py-2 sticky top-[42px] bg-white"></th>}
              </tr>
            )}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, index) => {
              const rowId = row.original ? (getRowId ? getRowId(row.original) : row.original.id) : row.id;
              const isSelected = selectedRowIds?.includes(rowId);
              return (
                <tr 
                  key={row.id} 
                  className={`
                    ${isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''} 
                    ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'} 
                    hover:bg-blue-50/50 hover:shadow-sm transition-all duration-150 border-b border-gray-100
                  `}
                >
                  {showSelection && onSelectedRowIdsChange && (
                    <td className="px-4 py-3 w-10">
                      <input 
                        type="checkbox" 
                        checked={!!isSelected} 
                        onChange={(e)=>{
                          if (!showSelection || !onSelectedRowIdsChange) return;
                          if (e.target.checked) onSelectedRowIdsChange([...(selectedRowIds || []), rowId]);
                          else onSelectedRowIdsChange((selectedRowIds || []).filter((id)=> id !== rowId));
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2"
                      />
                    </td>
                  )}
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3 text-sm text-gray-800 border-r border-gray-100 last:border-r-0">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                  {renderRowActions && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {renderRowActions(row.original)}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
            {memoData.length === 0 && (
              <tr>
                <td className="px-3 py-6 text-center text-sm text-gray-500" colSpan={(table.getAllColumns().length) + (onSelectedRowIdsChange ? 1 : 0) + (renderRowActions ? 1 : 0)}>
                  No data
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
        
        {/* Modern Pagination */}
        <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600 font-medium">
              Total: <span className="text-gray-900 font-semibold">{rowCount?.toLocaleString?.() ?? rowCount}</span> records
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Show:</label>
              <select 
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all" 
                value={pageSize} 
                onChange={(e)=> onPageSizeChange?.(parseInt(e.target.value))}
              >
                {[10,25,50,100].map((s)=>(<option key={s} value={s}>{s} per page</option>))}
              </select>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">
              Page <span className="font-semibold text-gray-900">{page + 1}</span> of{' '}
              <span className="font-semibold text-gray-900">{Math.max(1, Math.ceil((rowCount || 0) / (pageSize || 10)))}</span>
            </span>
            
            <div className="flex items-center gap-1">
              <button 
                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-400 transition-all flex items-center justify-center" 
                onClick={()=> onPageChange?.(0)} 
                disabled={page <= 0}
                title="First page"
              >
                <ChevronDoubleLeftIcon className="w-4 h-4" />
              </button>
              <button 
                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-400 transition-all flex items-center justify-center" 
                onClick={()=> onPageChange?.(Math.max(0, page - 1))} 
                disabled={page <= 0}
                title="Previous page"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <button 
                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-400 transition-all flex items-center justify-center" 
                onClick={()=> onPageChange?.(page + 1)} 
                disabled={(page + 1) >= Math.ceil((rowCount || 0) / (pageSize || 10))}
                title="Next page"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
              <button 
                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-400 transition-all flex items-center justify-center" 
                onClick={()=> onPageChange?.(Math.ceil((rowCount || 0) / (pageSize || 10)) - 1)} 
                disabled={(page + 1) >= Math.ceil((rowCount || 0) / (pageSize || 10))}
                title="Last page"
              >
                <ChevronDoubleRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TanTable;
